import { CSSProperties } from 'react'
import { IEmailEditorCanvas } from '@dataTypes/marketing.type'

const EMAIL_CANVAS_WRAPPER_CLASS = 'mt-marketing-email-builder-root'

export function isSeededMarketingEmailHtml(html: string): boolean {
  if (!html) return false
  return /(?:^|[\s"'=])mt-email-tpl(?:\s|--|"|'|>)/.test(html)
}

export function emailCanvasCssVars(
  canvas?: IEmailEditorCanvas | null,
): CSSProperties {
  if (!canvas) return {}
  const style: CSSProperties = {}
  const asVars = style as Record<string, string>
  if (canvas.page_color?.trim()) {
    style.backgroundColor = canvas.page_color.trim()
    asVars['--mt-email-page-bg'] = canvas.page_color.trim()
  }
  if (canvas.page_width?.trim()) {
    const w = /px|%|em|rem$/i.test(canvas.page_width.trim())
      ? canvas.page_width.trim()
      : `${canvas.page_width.trim()}px`
    asVars['--mt-email-page-width'] = w
  }
  if (canvas.header_band_color?.trim()) {
    asVars['--mt-email-header-band-bg'] = canvas.header_band_color.trim()
  }
  if (canvas.cta_button_bg_color?.trim()) {
    asVars['--mt-email-cta-btn-bg'] = canvas.cta_button_bg_color.trim()
  }
  if (canvas.cta_button_alt_bg_color?.trim()) {
    asVars['--mt-email-cta-btn-bg-alt'] = canvas.cta_button_alt_bg_color.trim()
  }
  if (canvas.cta_button_text_color?.trim()) {
    asVars['--mt-email-cta-btn-fg'] = canvas.cta_button_text_color.trim()
  }
  if (canvas.cta_button_alt_text_color?.trim()) {
    asVars['--mt-email-cta-btn-alt-fg'] =
      canvas.cta_button_alt_text_color.trim()
  }
  if (canvas.hero_copy_band_color?.trim()) {
    asVars['--mt-email-hero-copy-band-bg'] = canvas.hero_copy_band_color.trim()
  }
  return style
}

const canvasVarsToInlineStyle = (
  canvas?: IEmailEditorCanvas | null,
): string => {
  const parts: string[] = []
  if (canvas?.page_color?.trim()) {
    parts.push(`background-color:${canvas.page_color.trim()}`)
    parts.push(`--mt-email-page-bg:${canvas.page_color.trim()}`)
  }
  if (canvas?.page_width?.trim()) {
    const w = /px|%|em|rem$/i.test(canvas.page_width.trim())
      ? canvas.page_width.trim()
      : `${canvas.page_width.trim()}px`
    parts.push(`--mt-email-page-width:${w}`)
  }
  if (canvas?.header_band_color?.trim()) {
    parts.push(`--mt-email-header-band-bg:${canvas.header_band_color.trim()}`)
  }
  if (canvas?.cta_button_bg_color?.trim()) {
    parts.push(`--mt-email-cta-btn-bg:${canvas.cta_button_bg_color.trim()}`)
  }
  if (canvas?.cta_button_alt_bg_color?.trim()) {
    parts.push(
      `--mt-email-cta-btn-bg-alt:${canvas.cta_button_alt_bg_color.trim()}`,
    )
  }
  if (canvas?.cta_button_text_color?.trim()) {
    parts.push(`--mt-email-cta-btn-fg:${canvas.cta_button_text_color.trim()}`)
  }
  if (canvas?.cta_button_alt_text_color?.trim()) {
    parts.push(
      `--mt-email-cta-btn-alt-fg:${canvas.cta_button_alt_text_color.trim()}`,
    )
  }
  if (canvas?.hero_copy_band_color?.trim()) {
    parts.push(
      `--mt-email-hero-copy-band-bg:${canvas.hero_copy_band_color.trim()}`,
    )
  }
  return parts.join(';')
}

export function attachEmailCanvasToHtml(
  html: string,
  canvas?: IEmailEditorCanvas | null,
): string {
  if (!html || !canvas || isSeededMarketingEmailHtml(html)) return html
  const payload = JSON.stringify(canvas)
  const inline = canvasVarsToInlineStyle(canvas)
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    const existing = doc.body.querySelector(
      `[data-email-editor-canvas], .${EMAIL_CANVAS_WRAPPER_CLASS}`,
    ) as HTMLElement | null
    if (existing) {
      existing.setAttribute('data-email-editor-canvas', payload)
      if (inline) existing.setAttribute('style', inline)
      return doc.body.innerHTML
    }
    const wrap = doc.createElement('div')
    wrap.className = EMAIL_CANVAS_WRAPPER_CLASS
    wrap.setAttribute('data-email-editor-canvas', payload)
    if (inline) wrap.setAttribute('style', inline)
    while (doc.body.firstChild) wrap.appendChild(doc.body.firstChild)
    doc.body.appendChild(wrap)
    return doc.body.innerHTML
  } catch {
    return html
  }
}

/**
 * True when the RTE body has no meaningful content (email starter layouts only).
 */
export function isEmailBodyEffectivelyEmpty(html: string): boolean {
  if (!html || typeof html !== 'string') return true
  const normalized = html
    .replace(/&nbsp;/gi, '')
    .replace(/<br\s*\/?>/gi, '')
    .replace(/\s+/g, '')
    .trim()
  if (!normalized) return true
  return /^<p>\s*<\/p>$/i.test(normalized)
}

/**
 * Wraps major blocks using the same markup as `wrapEmailBlockInRow` (Marketing constants) so the
 * email row hover toolbar (move up / down / delete) applies to predefined mock templates.
 * Adds `e-rte-email-row--mock-section` for tighter spacing (see TemplatePreviewStyle.css).
 * Idempotent: skips nodes that are already `.e-rte-email-row`.
 */
export function wrapMockEmailTemplateSectionsInRows(html: string): string {
  if (!html || typeof html !== 'string' || !html.includes('mt-email-tpl')) {
    return html
  }

  try {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    const mock = doc.body.querySelector('.mt-email-tpl')
    if (!mock) return html

    const wrapDirectElementChildren = (parent: Element): void => {
      const toWrap: Element[] = []
      for (let i = 0; i < parent.childNodes.length; i++) {
        const node = parent.childNodes[i]
        if (node.nodeType !== Node.ELEMENT_NODE) continue
        const el = node as Element
        if (el.classList.contains('e-rte-email-row')) continue
        const tag = el.tagName.toUpperCase()
        if (tag === 'SCRIPT' || tag === 'STYLE') continue
        toWrap.push(el)
      }
      for (const el of toWrap) {
        const row = doc.createElement('div')
        row.setAttribute(
          'class',
          'e-rte-email-row e-rte-email-row--mock-section',
        )
        row.setAttribute('data-email-row', 'true')
        row.setAttribute('style', 'max-width:100%;')
        parent.insertBefore(row, el)
        row.appendChild(el)
      }
    }

    doc.body.querySelectorAll('.mt-email-tpl__content').forEach((el) => {
      wrapDirectElementChildren(el)
    })

    const structuralHosts = [
      '.mt-email-tpl__welcome-intro',
      '.mt-email-tpl__recall-shell',
      '.mt-email-tpl__thank-you-shell',
      '.mt-email-tpl__year-end-shell',
      '.mt-email-tpl__outreach-shell',
    ]
    structuralHosts.forEach((sel) => {
      doc.body.querySelectorAll(sel).forEach((el) => {
        wrapDirectElementChildren(el)
      })
    })

    wrapDirectElementChildren(mock)

    return doc.body.innerHTML
  } catch {
    return html
  }
}

/**
 * Reads `data-email-editor-canvas` from seeded/persisted email HTML (no DB column).
 */
export function parseEmailEditorCanvasFromHtml(
  html: string | undefined | null,
): IEmailEditorCanvas | undefined {
  if (!html || typeof html !== 'string') return undefined
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    const el = doc.body.querySelector('[data-email-editor-canvas]')
    const raw = el?.getAttribute('data-email-editor-canvas')
    if (!raw) return undefined
    const parsed = JSON.parse(raw) as IEmailEditorCanvas
    return parsed && typeof parsed === 'object' ? parsed : undefined
  } catch {
    return undefined
  }
}

/**
 * Strip tags for a plain-text version of an email body (draft / ESP paste).
 * Not a MIME multipart generator; collapses whitespace reasonably.
 */
export function emailHtmlToPlainText(html: string): string {
  if (!html || typeof html !== 'string') return ''
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    doc.querySelectorAll('script,style,noscript').forEach((el) => el.remove())
    const raw = doc.body.textContent ?? ''
    return raw
      .replace(/\u00a0/g, ' ')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/[ \t]{2,}/g, ' ')
      .trim()
  } catch {
    return html
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  }
}
